/**
 * Multi-select 領域 (area) editor — the multi-value sibling of
 * {@link AreaChipPicker}. Mirrors Backlog's multi-category model: a
 * single WBS row / task can belong to several 領域.
 *
 * Renders one colored chip per selected value (using the same
 * `areaBadgeClasses` palette as every other 領域 surface), each with a
 * remove (×) affordance. An "add" button opens a dropdown of options;
 * options already selected show a check and toggle off when clicked.
 *
 * Consumers own the Firestore write in `onChange`, which receives the
 * full next array.
 */
import { useEffect, useRef, useState } from 'react';
import { Check, Plus, X } from 'lucide-react';
import { areaBadgeClasses, type AreaColorIndexMap } from '../../lib/areaColor';
import { cn } from '../../lib/utils';

export interface AreaMultiPickerProps {
  /** Currently selected area labels. Empty array means "未設定". */
  value: string[];
  /** Project-wide area catalog labels. */
  options: readonly string[];
  /** `label -> stored colorIndex` lookup; see {@link AreaColorIndexMap}. */
  colorIndices?: AreaColorIndexMap;
  /** Fired with the full next selection whenever the user adds/removes. */
  onChange: (next: string[]) => void | Promise<void>;
  /** Optional label rendered before the chips (e.g. "領域:"). */
  label?: string | null;
  /** Optional `data-testid` override on the wrapping root. */
  testId?: string;
}

export function AreaMultiPicker({
  value,
  options,
  colorIndices,
  onChange,
  label = null,
  testId = 'area-multi-picker',
}: AreaMultiPickerProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current) return;
      if (ref.current.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const t = window.setTimeout(() => {
      document.addEventListener('mousedown', onDown);
      document.addEventListener('keydown', onKey);
    }, 0);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = (opt: string): void => {
    const next = value.includes(opt)
      ? value.filter((v) => v !== opt)
      : [...value, opt];
    void onChange(next);
  };

  const remove = (opt: string): void => {
    void onChange(value.filter((v) => v !== opt));
  };

  return (
    <div
      ref={ref}
      className="relative inline-flex flex-wrap items-center gap-1"
      data-testid={testId}
    >
      {label ? (
        <span className="text-[11px] text-slate-500">{label}</span>
      ) : null}
      {value.map((v) => (
        <span
          key={v}
          className={cn(
            'inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[11px] font-medium',
            areaBadgeClasses(v, colorIndices),
          )}
          data-testid={`${testId}-chip`}
        >
          {v}
          <button
            type="button"
            aria-label={`${v} を削除`}
            onClick={() => remove(v)}
            className="ml-0.5 inline-flex items-center rounded-sm hover:bg-black/10"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="領域を追加"
        className={cn(
          'inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[11px]',
          value.length === 0
            ? 'border border-dashed border-slate-300 text-slate-400'
            : 'text-slate-400 hover:text-slate-600',
        )}
        data-testid={`${testId}-add`}
      >
        <Plus className="h-3 w-3" />
        {value.length === 0 ? '領域を追加' : null}
      </button>
      {open ? (
        <div
          className="absolute left-0 top-full z-30 mt-1 max-h-72 w-56 overflow-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg"
          data-testid={`${testId}-options`}
        >
          {options.map((opt) => {
            const selected = value.includes(opt);
            return (
              <button
                key={opt}
                type="button"
                className={cn(
                  'flex w-full items-center gap-2 px-2 py-1 text-left text-xs hover:bg-slate-50',
                  selected ? 'bg-slate-100' : '',
                )}
                onClick={() => toggle(opt)}
                role="option"
                aria-selected={selected}
              >
                <span className="inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center">
                  {selected ? <Check className="h-3 w-3 text-slate-700" /> : null}
                </span>
                <span
                  className={cn(
                    'inline-block rounded px-1.5 py-0.5 text-[11px]',
                    areaBadgeClasses(opt, colorIndices),
                  )}
                >
                  {opt}
                </span>
              </button>
            );
          })}
          {options.length === 0 ? (
            <div className="px-2 py-2 text-center text-[11px] text-slate-400">
              選択肢がありません
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
